import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, sites } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'search-engine');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (slug && slug !== activeCategory) {
      setLoading(true);
      setActiveCategory(slug);
      const timer = setTimeout(() => setLoading(false), 150);
      return () => clearTimeout(timer);
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.id === activeCategory),
    [activeCategory],
  );

  const filteredSites = useMemo(
    () => sites.filter((s) => s.category === activeCategory),
    [activeCategory],
  );

  const categoryName = currentCategory?.name || '网址分类';

  const categorySchema = {
    name: '网址分类列表',
    url: `https://navlink2023.github.io/category/${activeCategory}`,
    description: `${categoryName} - 最新网址导航，收录${filteredSites.length}个${categoryName}平台官方最新网址入口`,
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://navlink2023.github.io/' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `https://navlink2023.github.io/category/${activeCategory}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${categoryName}最新网址入口`}
        description={`收录${categoryName}类平台官方最新网址入口共${filteredSites.length}个，实时更新维护，确保链接安全有效。涵盖${filteredSites.map(s => s.name).slice(0, 5).join('、')}等平台最新网址。`}
        keywords={`${categoryName},${categoryName}最新网址,${categoryName}最新网址入口,官方最新网址,最新网址大全`}
        canonicalUrl={`https://navlink2023.github.io/category/${activeCategory}`}
      />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="WebPage" data={categorySchema} />

      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto py-4">
            <Breadcrumb
              items={[
                { label: '网址分类', path: '/category/search-engine' },
                { label: categoryName },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6">
              <aside className="w-full lg:w-56 flex-shrink-0">
                <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-3 px-3">
                  网址分类
                </h3>
                <nav className="flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      onClick={() => { window.scrollTo({ top: 0, behavior: 'instant' }); }}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex-shrink-0 ${
                        activeCategory === cat.id
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </nav>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="mb-6">
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">
                    {categoryName}最新网址入口
                  </h1>
                  <p className="text-sm text-foreground-600">
                    共收录 <strong className="text-primary-600 font-semibold">{filteredSites.length}</strong> 个{categoryName}平台官方最新网址，实时更新，安全可靠
                  </p>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    {filteredSites.map((site) => (
                      <a
                        key={site.id}
                        href={site.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="group p-5 rounded-xl bg-background-100 hover:bg-background-200/50 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${site.iconBg} group-hover:scale-110 transition-transform duration-200`}>
                            <i className="ri-global-line text-lg text-background-50"></i>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 whitespace-nowrap">
                                {site.name}
                              </h3>
                              {site.isNew && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                                  新
                                </span>
                              )}
                              {site.isHot && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-100 text-primary-700 whitespace-nowrap">
                                  热
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                              {site.description}
                            </p>
                            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                              {site.tags.map((tag) => (
                                <span key={tag} className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}

                {!loading && filteredSites.length === 0 && (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mx-auto mb-4">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-sm text-foreground-500">该分类暂无收录网址</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}